/** Per-view sub-route registry — one place that knows how a view's open
 *  section is written into the URL and read back out.
 *
 *  Why this exists. Views address themselves as `/${view}` (see
 *  AuthenticatedController's `let url =`), but several have an inner selection
 *  that belongs in the address too: which settings page, which profile tab,
 *  which Sales section. Each was added by hand — a branch in the URL builder, a
 *  matching regex in the boot parser, a field in the history state and another
 *  in its comparison. Four places, kept in step by memory.
 *
 *  They drifted, as split grammars do. The builder wrote `/system-settings/
 *  general` while the parser treated `general` as the default; the parser
 *  accepted a two-level `integrations/xero` that the route table then called
 *  unknown and 404'd. Stating the grammar ONCE per view is the fix: build and
 *  parse come from the same declaration, so they cannot disagree, and the route
 *  table asks the registry what it accepts rather than restating it.
 *
 *  Deliberately NOT a router. It answers two narrow questions — given a view and
 *  its slug, what is the path; given a path, which view and slug is it — and
 *  owns no state, no history and no navigation.
 *
 *  Scope note: this keys off the VIEW, so it covers `/${view}/${slug}` shapes
 *  only. The project detail view's `documentRouteSlug` and `eventsRouteSlug`
 *  sit inside a pretty project path (`/projects/[ws]/[project]/documents/[doc]`)
 *  and are per-TAB rather than per-view, so they are not this registry's to
 *  absorb — see the note in App.tsx, which asks for a per-tab map for those.
 */

import { parsePersonalAccountPath } from './personalAccountRoutes';
import { isPreferenceSectionId } from '../features/preferences/preferenceSectionIds';

/** A view whose URL carries an inner selection. Keyed by AppView id. */
export interface SubRouteSpec {
  /** Path for this view with no sub-route selected — its canonical address. */
  base: string;
  /** Previous addresses accepted on read; every new link uses base. */
  aliases?: readonly string[];
  /** How many path segments the slug may span. Published so the route table can
   *  accept exactly this view's shape without restating the grammar. */
  depth: number;
  /** Build the path for a selected slug. Returning `base` for the default
   *  selection keeps the canonical address short, the way the employer profile
   *  leaves `details` off. */
  build: (slug: string) => string;
  /** Pull the slug back out of a path, or null if this path is not this view's.
   *  Must return null for the base path itself — "no selection" is not a slug. */
  parse: (pathname: string) => string | null;
  /** Rejects slugs the view does not have. The view still claims the address, but with no selection, so a typo cannot
   *  become a section the view then has to correct with a second history entry. */
  isValidSlug?: (slug: string) => boolean;
}

const trimTrailing = (p: string) => p.replace(/\/+$/, '') || '/';
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Build/parse a `/{base}/{slug}` shape.
 *
 *  `defaultSlug` is left off the URL so the view's canonical address stays the
 *  short one — the same rule the employer profile follows in leaving `details`
 *  off, and what the drill-in convention means by "the default section omitted".
 *
 *  `depth` is how many path segments the slug may span. Settings sections nest
 *  one level (`integrations/xero`, `workflows/trigger-catalog`) and the slug
 *  carries that slash, so their grammar is genuinely two segments wide. Stating
 *  it here is what stops the route table guessing: it reads the depth back off
 *  the spec instead of hard-coding a single segment and 404ing the nested pages. */
function segmentSpec(base: string, opts: { defaultSlug?: string; depth?: number; aliases?: readonly string[] } = {}): SubRouteSpec {
  const { defaultSlug, depth = 1, aliases = [] } = opts;
  const tail = Array.from({ length: depth }, (_, i) => (i === 0 ? '[^/]+' : '(?:/[^/]+)?')).join('');
  const patterns = [base, ...aliases].map(address => new RegExp(`^${escapeRe(address)}/(${tail})/?$`));
  return {
    base,
    aliases,
    depth,
    // Encoded per segment to mirror the decode in `parse` — an asymmetric round
    // trip would read back as a URL that never equals the one we pushed, which
    // the controller sees as a permanently stale address.
    build: (slug) =>
      !slug || slug === defaultSlug
        ? base
        : `${base}/${slug.split('/').map(encodeURIComponent).join('/')}`,
    parse: (pathname) => {
      const m = patterns.map(pattern => trimTrailing(pathname).match(pattern)).find(Boolean);
      // Decode per segment — a slug spanning two segments keeps its separator,
      // which decodeURIComponent on the whole string would leave alone anyway
      // but split/join makes the intent explicit and survives an encoded slash.
      return m ? m[1].split('/').map(decodeURIComponent).join('/') : null;
    },
  };
}

const profileSpec = segmentSpec('/profile', { defaultSlug: 'overview', depth: 3 });
const DOCUMENT_FOLDERS = /^\/profile\/(workspace\/documents(?:\/[^/]+)+)\/?$/;

/** The workspace file browser names its open folder in the address, any number of levels down. */
function withDocumentFolders(spec: SubRouteSpec): SubRouteSpec {
  return {
    ...spec,
    parse: (pathname) => {
      const folders = trimTrailing(pathname).match(DOCUMENT_FOLDERS);
      return folders ? folders[1].split('/').map(decodeURIComponent).join('/') : spec.parse(pathname);
    },
  };
}

/** The registry. Adding a view here is the whole change — no new prop, no new
 *  branch in the URL builder. */
export const SUB_ROUTES: Record<string, SubRouteSpec> = {
  // Sales hub sections. `overview` is the default and stays off the URL, so the
  // hub's canonical address is /sales.
  sales: segmentSpec('/sales', { defaultSlug: 'overview' }),

  // Purchases hub sections. Same drill-in as Sales, same default-slug rule.
  purchases: segmentSpec('/purchases', { defaultSlug: 'overview' }),

  // Finance hubs use the global sidebar for their sections. Keeping the open
  // section in the address makes refresh, Back, and shared links deterministic.
  payroll: segmentSpec('/payroll', { defaultSlug: 'overview' }),
  accounting: segmentSpec('/accounting', { defaultSlug: 'bank-accounts' }),
  tax: segmentSpec('/tax', { defaultSlug: 'vat-returns' }),

  // Settings sections usually nest one level (`integrations/xero`). Template
  // editors add one final readable segment (`templates/email/:templateId`) so
  // every opened template is refreshable and participates in browser history.
  // No default slug is elided: the bare `/system-settings` address is retired
  // (it used to be General) and redirects to Preferences, so the integrations
  // default has to be written out or a reload would bounce admins away.
  'system-settings': segmentSpec('/system-settings', { depth: 3 }),

  // Personal Preferences. No default slug either: with nothing selected the
  // address is the phone's section list, and desktop settles on the first
  // section by writing it into the URL.
  preferences: { ...segmentSpec('/preferences'), isValidSlug: isPreferenceSectionId },

  // Workspace administration is nested as /profile/workspace/[section].
  // Focused workspace workflows may add one final segment (for example
  // /profile/workspace/users/invite). Legacy personal /profile links are
  // accepted here and canonicalised to /account by the controller.
  // Workspace Documents add the open folder (workspace/documents/[folder]/...), which runs deeper than any other section.
  profile: withDocumentFolders(profileSpec),

  // Personal settings have their own customer-facing address. `/profile` is
  // retained above for workspace settings and as a legacy personal redirect;
  // the Account screen itself now reads as /account[/section].
  account: segmentSpec('/account', { defaultSlug: 'overview' }),

  // The open conversation is part of the address — /messages/[chatSlug] — so
  // refresh, Back/Forward and shared links land on the same thread. No
  // default slug: with nothing selected the canonical address is /messages.
  // Depth 2: a chat name alone collides across workspaces (two different
  // clients can each have a "John Smith" or a project named the same thing),
  // so an external/project chat's slug carries its workspace as a leading
  // segment — /messages/[workspace]/[chat] — the way project paths already
  // scope a project name under its client. Purely internal chats have no
  // such workspace and stay one segment, /messages/[chat].
  messages: segmentSpec('/messages', { depth: 2 }),

  // Plant register. No default slug: with nothing selected the canonical
  // address is /plant, and a drilled-in machine is /plant/[id] — the id
  // rather than a name slug, because the register is workspace-scoped data
  // fetched by PlantAssetsService, not part of the DataService cache the
  // other slug builders resolve names against.
  plant: segmentSpec('/plant'),

  // Only the Projects list belongs to Pipeline. Project detail keeps its
  // existing /projects/[workspace]/[project] grammar outside this registry.
  projects: {
    base: '/pipeline/projects',
    aliases: ['/projects'],
    depth: 0,
    build: () => '/pipeline/projects',
    parse: () => null,
  },

  // Outreach shares Pipeline navigation but keeps its existing view/grant id.
  // Old bookmarks remain readable; new links use the shared hub address.
  // Depth 2: the second segment is the drilled-in record, a batch under
  // approvals or a prospect under prospects, so /pipeline/outreach/approvals/[id]
  // keeps its section written out even though bare approvals is elided.
  outreach: segmentSpec('/pipeline/outreach', { defaultSlug: 'approvals', depth: 2, aliases: ['/outreach'] }),
};

/** The path for a view, with its sub-route slug applied. Falls back to
 *  `/${view}` for views with no spec, which is what the controller already
 *  does — so this is safe to call for any view. */
export function buildViewPath(view: string, slug?: string | null): string {
  const spec = SUB_ROUTES[view];
  if (!spec) return view === 'dashboard' ? '/' : `/${view}`;
  return slug ? spec.build(slug) : spec.base;
}

/** Which registered view a path belongs to, and the slug it selects.
 *  Returns null when no registered view claims it — the caller's existing
 *  parsers still own everything else. */
export function matchViewPath(pathname: string): { view: string; slug: string | null } | null {
  const path = trimTrailing(pathname);
  for (const [view, spec] of Object.entries(SUB_ROUTES)) {
    if ([spec.base, ...(spec.aliases ?? [])].some(base => path === trimTrailing(base))) return { view, slug: null };
    const slug = spec.parse(path);
    if (slug !== null) return { view, slug: spec.isValidSlug && !spec.isValidSlug(slug) ? null : slug };
  }
  return null;
}

/** Every path shape the registry can produce, for the route table to accept
 *  without restating the grammar. */
export function registeredSubRoutePatterns(): RegExp[] {
  return Object.values(SUB_ROUTES).flatMap((spec) => {
    const tail = Array.from({ length: spec.depth }, () => '(?:/[^/]+)?').join('');
    return [spec.base, ...(spec.aliases ?? [])].map(base => new RegExp(`^${escapeRe(base)}${tail}/?$`));
  });
}

const LEGACY_PREFERENCE_SLUGS = new Map([
  ['notifications', 'notifications'],
  ['devices', 'notifications'],
  ['privacy', 'privacy'],
]);

/** Where an address that used to hold a now-personal setting lives today, or
 *  null if the path is not one of them. System Settings > General and the
 *  Account Notifications, Devices and Privacy pages all moved into
 *  Preferences, and the retired Field devices page falls back to the plugin list; old bookmarks and shared links keep working through this.
 *
 *  Pure on purpose: the boot parser calls it first and replaces the history
 *  entry, so Back does not return to a dead address. */
export function resolveLegacyPreferencesPath(pathname: string): string | null {
  const path = trimTrailing(pathname);
  if (path === '/system-settings' || path === '/system-settings/general') return buildViewPath('preferences', 'appearance');
  // Quantis Field is retired, so its devices page went with it.
  if (path === '/system-settings/integrations/field-devices') return buildViewPath('system-settings', 'integrations');

  const personal = parsePersonalAccountPath(path);
  const bare = path.match(/^\/(?:account|profile)\/([^/]+)$/)?.[1];
  const slug = personal ? personal.section : bare ?? null;
  const target = slug ? LEGACY_PREFERENCE_SLUGS.get(slug) : undefined;
  return target ? buildViewPath('preferences', target) : null;
}
